import React, { useEffect, useMemo, useRef, useState } from "react";
import DotCanvas from "./DotCanvas";
import { paintBall } from "./paintBall";
import { COUNT_FONT, countInk, paintCount, rgbOf } from "./paintCount";
import { smooth } from "./reveal";
import { constellationFrame, constellationPlan, numberAt } from "./transitionFrames";
import { Hub, useBox } from "./shared";
import useMarkTargets from "./useMarkTargets";

// PROTOTYPE (hero shapes): Costellazione, how Cerchio goes from today to
// with AVRAH, in two breaths (breaths.js): the disc fills with gold as
// AVRAH reaches your clients, the count climbing in its middle; then the
// circle goes on into AVRAH's mark, its clients coming off it to draw it
// in two strokes, the count sealed into the mark's sphere, AVRAH's ball
// (paintBall.js), before they land round it: it shrinks to its size on the
// ball, the ball grows in under it, and the count is set flat on it in dark
// ink as the ball reaches it (transitionFrames.js). The count and the ball are painted on the
// clients' canvas (paintCount.js); the count in the markup only keeps its
// room and its size. It draws in the disc's box: `disc` is the disc's
// layout, null until the box is measured. `glow`, while the hero invites
// the visitor to play it, warms the clients still waiting (DotCanvas.jsx).

// Until the count is measured, the mark's places for its clients have come
// (useMarkTargets.js) and the count's font has loaded, the clients stay on
// the disc and the count, in the markup, in its hollow.
export function Constellation({ story, disc, glow }) {
  const [countRef, count] = useBox();
  const targets = useMarkTargets(disc && disc.dots.length);
  const plan = useMemo(() => disc && count && targets && constellationPlan(disc, { count, targets }), [disc, count, targets]);
  const lit = useEased(story.lit ? 1 : 0, LIT_MS);
  const font = useCountFont();
  const painting = Boolean(plan && font);
  const frame = painting && constellationFrame(plan, story);
  const over = painting ? (ctx) => paintNumber(ctx, { plan, at: numberAt(plan, story), count: story.count, size: count.height, lit }) : undefined;
  return (
    <>
      <DotCanvas className="hs-dots hd-dots" layout={frame || disc} met={story.met} gold={story.gold} apart over={over} glow={glow} />
      <Hub story={story} countRef={countRef} data-painted={painting || undefined} />
    </>
  );
}

// The ball, in the gold button's golds at rest and pressed, and the
// count over it: `size` the count's font size as it is laid out, `lit` how
// far it has turned gold.
function paintNumber(ctx, { plan, at, count, size, lit }) {
  const { sphere, middle } = plan;
  const golds = { rest: tokenOf(ctx.canvas, "--gold"), shade: tokenOf(ctx.canvas, "--gold-pressed") };
  const on = { x: sphere.x, y: sphere.y, r: at.sphere };
  paintBall(ctx, on, golds);
  paintCount(ctx, {
    text: String(count),
    size: size * at.scale,
    x: middle,
    y: middle,
    ink: countInk({ white: rgbOf(tokenOf(ctx.canvas, "--fg")), lit }),
    ball: on,
    printed: tokenOf(ctx.canvas, "--gold-ink"),
  });
}

// A colour token of the page, `name`, as the canvas has it: #rrggbb, as
// all of the page's are.
const tokens = new WeakMap();
function tokenOf(canvas, name) {
  if (!tokens.has(canvas)) tokens.set(canvas, new Map());
  const known = tokens.get(canvas);
  if (!known.has(name)) {
    const token = getComputedStyle(canvas).getPropertyValue(name).trim();
    rgbOf(token);
    known.set(name, token);
  }
  return known.get(name);
}

// Whether the count's font has loaded, so the canvas sets it in its own
// face; where the browser cannot say, it is taken as loaded. It is always
// asked to load: Chrome's document.fonts.check() says yes while the face is
// still on its way, and the canvas would set the count in its fallback. It
// starts unloaded, in the browser as in the prerendered page, which has no
// document to ask.
function useCountFont() {
  const face = `${COUNT_FONT.weight} 1em ${COUNT_FONT.family}`;
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (loaded) return undefined;
    if (!document.fonts) {
      setLoaded(true);
      return undefined;
    }
    let live = true;
    document.fonts.load(face, "0123456789").then(
      () => live && setLoaded(true),
      (error) => {
        console.error(`Costellazione: the count's font did not load, so it is set in the fallback`, error);
        if (live) setLoaded(true);
      },
    );
    return () => {
      live = false;
    };
  }, [face, loaded]);
  return loaded;
}

// How long the count takes to turn gold, as the markup's did (heroV2.css).
const LIT_MS = 300;

// `target`, 0 or 1, eased toward over `ms` each time it changes; at once
// for those who ask for less motion.
function useEased(target, ms) {
  const [value, setValue] = useState(target);
  const valueRef = useRef(target);
  useEffect(() => {
    const from = valueRef.current;
    const set = (next) => {
      valueRef.current = next;
      setValue(next);
    };
    if (from === target) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      set(target);
      return undefined;
    }
    const start = performance.now();
    let frame = requestAnimationFrame(function step(now) {
      const t = Math.min(1, (now - start) / ms);
      set(from + (target - from) * smooth(t));
      if (t < 1) frame = requestAnimationFrame(step);
    });
    return () => cancelAnimationFrame(frame);
  }, [target, ms]);
  return value;
}
